import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer, NodeViewWrapper } from '@tiptap/react';
import {
  Box,
  Paper,
  IconButton,
  TextField,
  Button,
  Divider,
  Typography,
} from '@mui/material';
import { VideoLibrary, Delete, Add } from '@mui/icons-material';
import { useState } from 'react';

const VideoComponent = (props: any) => {
  const { node, updateAttributes, deleteNode } = props;
  const {
    url,
    preview_url,
    txt,
    presentation,
    summary,
    timecodes = [],
  } = node.attrs;
  const [localTimecodes, setLocalTimecodes] = useState(timecodes || []);

  const addTimecode = () => {
    const newTimecodes = [...localTimecodes, { time: 0, name: '' }];
    setLocalTimecodes(newTimecodes);
    updateAttributes({ timecodes: newTimecodes });
  };

  const updateTimecode = (index: number, field: string, value: any) => {
    const newTimecodes = [...localTimecodes];
    newTimecodes[index] = { ...newTimecodes[index], [field]: value };
    setLocalTimecodes(newTimecodes);
    updateAttributes({ timecodes: newTimecodes });
  };

  const removeTimecode = (index: number) => {
    const newTimecodes = localTimecodes.filter(
      (_: any, i: number) => i !== index
    );
    setLocalTimecodes(newTimecodes);
    updateAttributes({ timecodes: newTimecodes });
  };

  return (
    <NodeViewWrapper>
      <Paper
        elevation={2}
        sx={{
          p: 2,
          my: 2,
          backgroundColor: '#f5f5f5',
          border: '2px solid #f44336',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <VideoLibrary color="error" />
          <strong>Видео блок</strong>
          <IconButton
            size="small"
            color="error"
            onClick={deleteNode}
            sx={{ ml: 'auto' }}
          >
            <Delete />
          </IconButton>
        </Box>

        <TextField
          fullWidth
          size="small"
          label="URL видео"
          value={url || ''}
          onChange={(e) => updateAttributes({ url: e.target.value })}
          sx={{ mb: 1 }}
        />

        <TextField
          fullWidth
          size="small"
          label="Preview URL (шапка для видео)"
          value={preview_url || ''}
          onChange={(e) => updateAttributes({ preview_url: e.target.value })}
          sx={{ mb: 1 }}
        />

        <TextField
          fullWidth
          size="small"
          label="Ссылка на txt расшифровку"
          value={txt || ''}
          onChange={(e) => updateAttributes({ txt: e.target.value })}
          sx={{ mb: 1 }}
          helperText="Ссылка на файл с текстовой расшифровкой видео"
        />

        <TextField
          fullWidth
          size="small"
          label="Ссылка на презентацию"
          value={presentation || ''}
          onChange={(e) => updateAttributes({ presentation: e.target.value })}
          sx={{ mb: 1 }}
          helperText="Ссылка на презентацию к видео"
        />

        <TextField
          fullWidth
          size="small"
          label="Ссылка на конспект"
          value={summary || ''}
          onChange={(e) => updateAttributes({ summary: e.target.value })}
          sx={{ mb: 1 }}
          helperText="Ссылка на конспект материала"
        />

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Таймкоды
        </Typography>

        {localTimecodes.map((tc: any, index: number) => (
          <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1 }}>
            <TextField
              size="small"
              label="Время (сек)"
              type="number"
              value={tc.time || 0}
              onChange={(e) =>
                updateTimecode(index, 'time', parseInt(e.target.value))
              }
              sx={{ width: '150px' }}
            />
            <TextField
              size="small"
              label="Метка"
              value={tc.name || ''}
              onChange={(e) => updateTimecode(index, 'name', e.target.value)}
              sx={{ flexGrow: 1 }}
            />
            <IconButton
              size="small"
              color="error"
              onClick={() => removeTimecode(index)}
            >
              <Delete />
            </IconButton>
          </Box>
        ))}

        <Button
          size="small"
          startIcon={<Add />}
          onClick={addTimecode}
          variant="outlined"
        >
          Добавить таймкод
        </Button>
      </Paper>
    </NodeViewWrapper>
  );
};

export const VideoBlock = Node.create({
  name: 'videoBlock',
  group: 'block',
  atom: true,

  addAttributes() {
    return {
      url: { default: '' },
      preview_url: { default: '' },
      txt: { default: '' },
      presentation: { default: '' },
      summary: { default: '' },
      timecodes: { default: [] },
      is_blur: { default: true },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-type="videoBlock"]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes({ 'data-type': 'videoBlock' }, HTMLAttributes),
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(VideoComponent);
  },

  // @ts-ignore - Tiptap type issue with custom commands
  addCommands() {
    return {
      insertVideoBlock:
        (attrs: {
          url?: string;
          preview_url?: string;
          txt?: string;
          presentation?: string;
          summary?: string;
          timecodes?: any[];
          is_blur?: boolean;
        }) =>
        ({ commands }: any) =>
          commands.insertContent({
            type: this.name,
            attrs,
          }),
    };
  },
});
